import { BrainCircuit, X } from 'lucide-react';
import { Suspense, lazy, useCallback, useState } from 'react';
import { useHoldNewBuildReload } from '@iqd/shared-app';
import { useHasAnyRole, useHostAuth } from '../../providers/host-auth-context';
import { DialogActions, DialogShell } from '../../shared/components/dialog';
import './ask-derek.css';
import { BTN_SIZE } from './launcher-geometry';
import { useChat } from './use-chat';
import { useLauncher } from './use-launcher';

/**
 * Ask Derek — the assistant that sits on every screen.
 *
 * Unlike the rest of this app it is not a route. The Angular host draws it
 * beside the router outlet on every page, so it is mounted on its own from
 * `remote-entry` rather than through `App`, and the two are independent: the
 * conversation carries on across a navigation, whichever framework owns the
 * screen being navigated to.
 *
 * The role is checked here as well as by the host that mounts it — the host
 * decides whether to pay for the mount, this decides whether to draw. Neither
 * is a security boundary; the insight API authorises the token for itself.
 */

export const DEREK_ROLE = 'derek-ai-user';

/**
 * The window is all of Ask Derek's weight — the markdown renderer above all,
 * plus the history panel and the composer. The launcher is drawn on every
 * page; the window is almost never opened on any one of them, so it is fetched
 * on the first open rather than bundled into the chunk every page waits on.
 *
 * Mounted from that first open onwards, never unmounted, so the window keeps
 * its own state across a close — see the note in `chat-window`.
 */
const importChatWindow = () => import('./components/chat-window');

const ChatWindow = lazy(() =>
  importChatWindow().then((module) => ({ default: module.ChatWindow })),
);

/**
 * Starts the window's chunk before it is asked for.
 *
 * Pointing at the launcher is the earliest sign anyone is about to open it, and
 * the chunk usually lands before the click does — so the lazy mount above has
 * nothing to wait for. Repeat calls are free: a module already requested comes
 * back from the registry rather than the network. Failure is ignored here
 * because this is only a head start; the real load is the `lazy` above, and it
 * reports through the error boundary the way it would have anyway.
 */
function warmChatWindow() {
  importChatWindow().catch(() => undefined);
}

export function AskDerek() {
  const { isAuthenticated } = useHostAuth();
  const hasRole = useHasAnyRole([DEREK_ROLE]);
  const [isOpen, setIsOpen] = useState(false);
  // A reload would throw away the question being typed and any answer still arriving.
  useHoldNewBuildReload(isOpen);
  // Latches on the first open: the window is lazily loaded, and once it is
  // here it stays, closed or not.
  const [hasOpened, setHasOpened] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);

  const launcher = useLauncher();
  const chat = useChat();
  const { thread, startNewThread, loadThreads, openThread, removeThread } = chat;

  const toggleOpen = useCallback(() => {
    setHasOpened(true);
    setIsOpen((open) => {
      // The first open starts the thread the welcome message stands in for.
      if (!open && !thread) startNewThread();
      return !open;
    });
  }, [startNewThread, thread]);

  const onLauncherClick = useCallback(() => {
    // The same button drags and opens; a click that ended a drag is not one.
    if (launcher.wasDragged()) return;
    toggleOpen();
  }, [launcher, toggleOpen]);

  const toggleHistory = useCallback(() => {
    setShowHistory((showing) => {
      if (!showing) loadThreads();
      return !showing;
    });
  }, [loadThreads]);

  const onNewChat = useCallback(() => {
    setShowHistory(false);
    startNewThread();
  }, [startNewThread]);

  const onSend = useCallback(
    (message: string) => {
      // Sending from the history list would otherwise leave the reply
      // streaming behind a screen that does not show it.
      setShowHistory(false);
      chat.sendMessage(message);
    },
    [chat],
  );

  const onOpenThread = useCallback(
    (threadId: string) => {
      setShowHistory(false);
      openThread(threadId);
    },
    [openThread],
  );

  if (!isAuthenticated || !hasRole) return null;

  return (
    <>
      {/* On a phone the window covers the screen and the launcher would sit on
          top of the send button, which is where the Angular screen left it —
          unreachable. The header's own close button is the way out there. */}
      {!(launcher.isMobile && isOpen) && (
        <button
          type="button"
          aria-label={isOpen ? 'Close Ask Derek' : 'Open Ask Derek'}
          aria-expanded={isOpen}
          onPointerDown={launcher.onPointerDown}
          onPointerEnter={warmChatWindow}
          onFocus={warmChatWindow}
          onClick={onLauncherClick}
          style={
            launcher.isMobile
              ? { right: 16, bottom: 16, width: 56, height: 56 }
              : {
                  left: launcher.position.x,
                  top: launcher.position.y,
                  width: BTN_SIZE,
                  height: BTN_SIZE,
                }
          }
          className={`fixed z-[1050] flex touch-none items-center justify-center rounded-full text-lg text-white shadow-lg transition-colors ${
            isOpen ? 'bg-hilevel-slate' : 'bg-hilevel-red'
          } ${launcher.isDragging ? 'cursor-grabbing' : 'cursor-pointer hover:opacity-90'}`}
        >
          {isOpen ? <X /> : <BrainCircuit />}
        </button>
      )}

      {/* Full screen on a phone, so the page behind it is dimmed and tapping
          out closes it. Off on anything wider, where the window is a panel. */}
      {launcher.isMobile && (
        <div
          onClick={toggleOpen}
          className={`fixed inset-0 z-[1048] bg-black/50 transition-opacity duration-300 ${
            isOpen ? 'visible opacity-100' : 'invisible opacity-0'
          }`}
        />
      )}

      {hasOpened && (
        <Suspense fallback={null}>
          <ChatWindow
            open={isOpen}
            isMobile={launcher.isMobile}
            isDragging={launcher.isDragging}
            position={launcher.windowPosition}
            thread={thread}
            isSending={chat.isSending}
            error={chat.error}
            showHistory={showHistory}
            threads={chat.threads}
            isLoadingThreads={chat.isLoadingThreads}
            onToggleHistory={toggleHistory}
            onNewChat={onNewChat}
            onClose={toggleOpen}
            onSend={onSend}
            onOpenThread={onOpenThread}
            onDeleteThread={setPendingDelete}
          />
        </Suspense>
      )}

      <DialogShell
        open={pendingDelete !== null}
        title="Delete this conversation?"
        onClose={() => setPendingDelete(null)}
        width="max-w-sm"
        footer={
          <DialogActions
            onCancel={() => setPendingDelete(null)}
            onConfirm={() => {
              if (pendingDelete) removeThread(pendingDelete);
              setPendingDelete(null);
            }}
            confirmLabel="Delete"
            busyLabel="Deleting…"
          />
        }
      >
        <p className="text-sm text-hilevel-slate">
          This conversation and everything in it will be removed. This cannot be undone.
        </p>
      </DialogShell>
    </>
  );
}
